import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Layers,
  Warehouse,
  ShoppingCart,
  Users,
  History,
  Settings,
  LogOut,
  Search,
  TreeDeciduous,
  Menu,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useCommandPalette } from '@/components/command-palette/command-palette-provider';
import { CommandPaletteTrigger } from '@/components/command-palette/command-palette-trigger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/products', label: 'Products', icon: Package },
  { to: '/variants', label: 'Variants', icon: Layers },
  { to: '/inventory', label: 'Inventory', icon: Warehouse },
  { to: '/orders', label: 'Orders', icon: ShoppingCart },
  { to: '/customers', label: 'Customers', icon: Users },
  { to: '/sync-history', label: 'Sync History', icon: History },
  { to: '/settings', label: 'Settings', icon: Settings },
];

export function Sidebar() {
  const { user, signOut } = useAuth();
  const { open } = useCommandPalette();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleOpenPalette = () => {
    setMobileOpen(false);
    open();
  };

  const handleSignOut = () => {
    setMobileOpen(false);
    signOut();
  };

  return (
    <>
      <div className="flex h-14 w-full shrink-0 items-center justify-between gap-3 border-b border-sidebar-border bg-sidebar px-3 md:hidden">
        <button
          onClick={() => setMobileOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-sidebar-foreground transition-colors hover:bg-accent"
          aria-label="Open navigation"
        >
          <Menu className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <TreeDeciduous className="h-5 w-5 shrink-0 text-primary" />
          <span className="truncate text-sm font-semibold tracking-tight text-sidebar-foreground">
            Dancing Oaks
          </span>
        </div>
        <button
          onClick={handleOpenPalette}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Open search"
        >
          <Search className="h-4 w-4" />
        </button>
      </div>

      <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogContent className="left-0 top-0 h-dvh w-[min(84vw,300px)] max-w-[300px] translate-x-0 translate-y-0 rounded-none border-r border-border p-0 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-0 md:hidden">
          <DialogHeader className="border-b border-sidebar-border px-5 py-4 text-left">
            <DialogTitle className="flex items-center gap-2 text-sm">
              <TreeDeciduous className="h-5 w-5 text-primary" />
              Dancing Oaks
            </DialogTitle>
            <DialogDescription className="sr-only">
              Mobile navigation menu.
            </DialogDescription>
          </DialogHeader>

          <div className="px-3 pt-3 pb-1">
            <button
              onClick={handleOpenPalette}
              className={cn(
                'flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-2',
                'text-[13px] text-muted-foreground transition-colors hover:bg-accent',
              )}
            >
              <Search className="h-3.5 w-3.5" />
              <span className="flex-1 text-left">Search...</span>
            </button>
          </div>

          <nav className="flex-1 space-y-0.5 px-3 py-3">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-sidebar-foreground',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="space-y-2 border-t border-sidebar-border px-3 py-3">
            {user && (
              <div className="px-2.5">
                <p className="truncate text-[12px] text-muted-foreground">{user.email}</p>
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-sidebar-foreground"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
            <p className="px-2.5 text-[10px] text-muted-foreground/50">v0.2.0-{__COMMIT_HASH__}</p>
          </div>
        </DialogContent>
      </Dialog>

      <aside className="hidden h-full w-[240px] shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <div className="flex h-14 items-center gap-2.5 border-b border-sidebar-border px-5">
          <TreeDeciduous className="h-5 w-5 text-primary" />
          <span className="text-sm font-semibold tracking-tight text-sidebar-foreground">
            Dancing Oaks
          </span>
        </div>

        <div className="px-3 pb-1 pt-3">
          <button
            onClick={open}
            className={cn(
              'flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-1.5',
              'text-[13px] text-muted-foreground transition-colors hover:bg-accent',
            )}
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1 text-left">Search...</span>
            <CommandPaletteTrigger />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 px-3 py-3">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-sidebar-foreground',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="space-y-2 border-t border-sidebar-border px-3 py-3">
          {user && (
            <div className="px-2.5">
              <p className="truncate text-[12px] text-muted-foreground">{user.email}</p>
            </div>
          )}
          <button
            onClick={signOut}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-sidebar-foreground"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
          <p className="px-2.5 text-[10px] text-muted-foreground/50">v0.2.0-{__COMMIT_HASH__}</p>
        </div>
      </aside>
    </>
  );
}
